import { keepPreviousData, skipToken, SkipToken } from '@tanstack/react-query';
import { Language } from '../../core/i18n/constants';
import { useLanguage } from '../../core/i18n/hooks/use-language';
import { BASE_BACKEND_QUERY_KEY, BASE_BACKEND_URL, QUERY_KEYS } from '../backend/constants';
import { QueryOptions, useAuthenticatedQuery } from '../backend/hooks/use-authenticated-query';
import {
  GetArticleAvailabilityResponse,
  GetArticleDetailPreCartResponse,
  GetArticleDetailResponse,
  GetArticlesResponse,
  GetFilterReferencesResponse,
  GetArticlesBatchResponse,
} from '../backend/openapi/api-types';
import { createOrderIndependentQueryKey } from '../backend/services/create-order-independent-query-key';
import { createSearchParams } from '../backend/services/create-search-params';

function toArticleIdsParams(articleIds: string[]): string {
  if (!articleIds.length) return '';
  return `${articleIds.map((id) => `articleIds=${id}`).join('&')}`;
}

const articleUrls = {
  getFilterReferences: () => `${BASE_BACKEND_URL}/articles/filter-references`,
  findArticles: (params: URLSearchParams) => `${BASE_BACKEND_URL}/articles?${params.toString()}`,
  getArticleDetail: (articleId: string) => `${BASE_BACKEND_URL}/articles/${articleId}`,
  getArticleAvailability: (articleId: string) => `${BASE_BACKEND_URL}/order/articles/${articleId}/availability`,
  getArticlePreCart: (articleId: string, params: URLSearchParams) =>
    `${BASE_BACKEND_URL}/order/articles/${articleId}?${params.toString()}`,
  getArticlesBatch: (articleIds: string[]) => `${BASE_BACKEND_URL}/articles-batch?${toArticleIdsParams(articleIds)}`,
};

export type UseFindArticlesOptions = {
  query: string;
  limit: number;
  offset: number;
  glosses?: string[];
  surfaces?: string[];
  categories?: string[];
  colorRanges?: string[];
  weatherResistances?: string[];
  effects?: string[];
  seriesNumbers?: string[];
  substrates?: string[];
  specialProperties?: string[];
  certificates?: string[];
};

export type UseArticlePreCartOptions = {
  weightKg: number;
  container?: string;
};

export type UseArticlesBatchOptions = {
  keepPreviousData?: boolean;
};

const articleQueryKeys = {
  filterReferences: (language: Language) => [BASE_BACKEND_QUERY_KEY, language, QUERY_KEYS.article, 'filter-references'],
  findArticles: (language: Language, params: UseFindArticlesOptions) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.article,
    'find-articles',
    createOrderIndependentQueryKey(params),
  ],
  articleDetail: (language: Language, articleId: string) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.article,
    articleId,
  ],
  articleAvailability: (language: Language, articleId: string) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.article,
    articleId,
    'availability',
  ],
  articlePreCart: (language: Language, articleId: string, params: UseArticlePreCartOptions) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.orders,
    QUERY_KEYS.article,
    articleId,
    createOrderIndependentQueryKey(params),
  ],
  articlesBatch: (language: Language, articleIds: string[], params: UseArticlesBatchOptions = {}) => [
    BASE_BACKEND_QUERY_KEY,
    language,
    QUERY_KEYS.article,
    articleIds,
    { ...params },
  ],
};

export const useFilterReferencesQuery = () => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetFilterReferencesResponse> = {
    options: {
      queryKey: articleQueryKeys.filterReferences(language),
    },
    url: articleUrls.getFilterReferences(),
  };
  return useAuthenticatedQuery<GetFilterReferencesResponse>(queryOptions);
};

export const useFindArticlesQuery = (params: UseFindArticlesOptions | SkipToken) => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetArticlesResponse> | SkipToken =
    params === skipToken
      ? skipToken
      : {
          options: {
            queryKey: articleQueryKeys.findArticles(language, params),
            placeholderData: (previousData) => previousData,
          },
          url: articleUrls.findArticles(createSearchParams(params)),
        };
  return useAuthenticatedQuery<GetArticlesResponse>(queryOptions);
};

export const useArticleDetailQuery = (articleId: string) => {
  const language = useLanguage();
  return useAuthenticatedQuery<GetArticleDetailResponse>({
    options: {
      queryKey: articleQueryKeys.articleDetail(language, articleId),
    },
    url: articleUrls.getArticleDetail(articleId),
  });
};

export const useArticleAvailabilityQuery = (articleId: string) => {
  const language = useLanguage();
  return useAuthenticatedQuery<GetArticleAvailabilityResponse>({
    options: {
      queryKey: articleQueryKeys.articleAvailability(language, articleId),
    },
    url: articleUrls.getArticleAvailability(articleId),
  });
};

export const useArticlePreCartQuery = (articleId: string, params: UseArticlePreCartOptions | SkipToken) => {
  const language = useLanguage();
  const queryOptions: QueryOptions<GetArticleDetailPreCartResponse> | SkipToken =
    params === skipToken
      ? skipToken
      : {
          options: {
            queryKey: articleQueryKeys.articlePreCart(language, articleId, params),
          },
          url: articleUrls.getArticlePreCart(articleId, createSearchParams(params)),
        };
  return useAuthenticatedQuery<GetArticleDetailPreCartResponse>(queryOptions);
};

export const useArticlesBatchQuery = (articleIds: string[], params?: UseArticlesBatchOptions | SkipToken) => {
  const language = useLanguage();

  const queryOptions: QueryOptions<GetArticlesBatchResponse> | SkipToken =
    params === skipToken
      ? skipToken
      : {
          options: {
            queryKey: articleQueryKeys.articlesBatch(language, articleIds, params),
            placeholderData: params?.keepPreviousData ? keepPreviousData : undefined,
          },
          url: articleUrls.getArticlesBatch(articleIds),
        };

  return useAuthenticatedQuery<GetArticlesBatchResponse>(queryOptions);
};
